import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { Checkbox } from 'components/ui/checkbox';
import { ReactNode, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { CONFIGURATION_CARD_CLASS } from '../configurationCard';

interface CaseClosingSubsectionProps {
    titleId: string;
    isEditMode: boolean;
    isSaving: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    children: ReactNode;
}

export function CaseClosingSubsection({
    titleId,
    isEditMode,
    isSaving,
    onEdit,
    onCancel,
    onSave,
    children,
}: CaseClosingSubsectionProps) {
    return (
        <section className="flex flex-col gap-2">
            <div className="flex min-h-8 items-center justify-between gap-4">
                <h4 className="text-h5">
                    <FormattedMessage id={titleId} />
                </h4>
                {isEditMode ? (
                    <div className="flex gap-2">
                        <CancelButton onClick={onCancel} disabled={isSaving} />
                        <SaveButton onClick={onSave} disabled={isSaving} />
                    </div>
                ) : (
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        onClick={onEdit}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                )}
            </div>
            <div className={cn(CONFIGURATION_CARD_CLASS, 'flex flex-col gap-4 p-4')}>{children}</div>
        </section>
    );
}

interface CaseClosingTickProps {
    label: string;
    checked: boolean;
    disabled: boolean;
    onCheckedChange?: (checked: boolean) => void;
    helperText?: string;
    trailing?: ReactNode;
}

export function CaseClosingTick({
    label,
    checked,
    disabled,
    onCheckedChange,
    helperText,
    trailing,
}: CaseClosingTickProps) {
    const checkboxId = useId();
    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
                <Checkbox
                    id={checkboxId}
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(value) => onCheckedChange?.(value)}
                />
                <label htmlFor={checkboxId} className={cn('text-body1', !disabled && 'cursor-pointer')}>
                    {label}
                </label>
                {trailing}
            </div>
            {helperText ? <p className="text-body2 text-fg-secondary pl-6">{helperText}</p> : null}
        </div>
    );
}
